import { Injectable } from '@angular/core';
import { HttpResponse, HttpStatusEnum } from '@core/api';
import { RolesAndPermissionsService } from '@core/services/roles-and-permissions.service';
import { UserGroup } from '@shared/interfaces';
import {
    GroupGeneralSettings,
    GroupGeneralSettingsDTO,
    SearchedUser,
    SearchedUserDTO,
} from '../../group-settings/_core/interfaces';
import { GroupMember } from '../../group-settings/_core/interfaces/member.interface';
import { GroupUserStatusEnum } from '../../groups-management/_core/enums';
import {
    CreateNewGroupDTO,
    CreateNewGroupResponseData,
    GroupsListOperations,
    JoinExistingGroupDTO,
    JoinExistingGroupResponseData,
} from '../../groups-management/_core/interfaces';
import {
    CurrentGroupInfoDTO,
    GroupDTO,
    GroupListDTO,
    GroupListPayload,
    GroupListResponseData,
    GroupPricePrepareData,
    GroupPricePrepareDataDTO,
    GroupSettingsHeader,
    GroupSettingsHeaderDTO,
    PrepareGroupsListData,
    PrepareGroupsListDTO,
} from '../interfaces';

@Injectable({ providedIn: 'root' })
export class GroupsMapperService {
    constructor(private rolesAndPermissionsService: RolesAndPermissionsService) {}
    public mapGetGroupMembers(data: any): HttpResponse<GroupMember[]> {
        return {
            status: HttpStatusEnum.SUCCESS,
            data: data.map((user: any) => {
                return {
                    userId: user.id,
                    userName: user.firstName + ' ' + (user.lastName ? user.lastName[0] + '.' : ''),
                    email: user.email,
                    groupRole: user.groupRole,
                    isGroupOwner: user.isOwner,
                    memberAffiliation: user.affiliationStatus,
                    roleError: false,
                    initialGroupRole: user.groupRole,
                };
            }),
        };
    }

    public mapSearchedUsersFromDTOtoModel(response: SearchedUserDTO): HttpResponse<SearchedUser[]> {
        return {
            status: HttpStatusEnum.SUCCESS,
            data: response.users.map(user => {
                return {
                    email: user.email,
                    firstName: user.firstName,
                    id: user.id,
                    lastName: user.lastName,
                };
            }),
        };
    }

    public mapGroupSettingsHeaderDTOtoModel(
        response: GroupSettingsHeaderDTO,
        currentUserId: number
    ): HttpResponse<GroupSettingsHeader> {
        return {
            status: HttpStatusEnum.SUCCESS,
            data: {
                id: response.id,
                enrollmentKey: response.enrollmentKey,
                name: response.name,
                isCurrentUserOwner: response.owner?.externalId === currentUserId,
                ownerName: response.owner ? `${response.owner.firstName} ${response.owner.lastName}` : null,
                canOwnershipBeTransferred: response.canOwnershipBeTransferred,
                isActive: response.isActive,
                role: response.role,
            },
        };
    }

    public mapGetListData(groupsListDTO: GroupListDTO): HttpResponse<GroupListResponseData> {
        return {
            status: HttpStatusEnum.SUCCESS,
            data: {
                totalFilteredGroups: groupsListDTO.totalCount,
                groupList: groupsListDTO.groups.map((group: GroupDTO) => {
                    return {
                        id: group.id,
                        name: group.name,
                        description: group.description,
                        numberOfMembers: group.numberOfMembers,
                        createdAt: group.createdAt,
                        labName: group.laboratory.name,
                        groupOwner: group.owner
                            ? {
                                  externalId: group.owner.externalId,
                                  fullName: `${group.owner.firstName} ${group.owner.lastName}`,
                                  email: group.owner.email,
                              }
                            : null,
                        groupUserStatus: !group.isActive ? GroupUserStatusEnum.DELETED : group.userStatus,
                        canCurrentUserAccessGroup: this.rolesAndPermissionsService.canAccessGroup({
                            hasJoinedGroup: group.userStatus === GroupUserStatusEnum.JOINED,
                            isGroupActive: group.isActive,
                            isGroupOwner: group.isOwner,
                            userRoleInGroup: group.role,
                        }),
                        isOwner: group.isOwner,
                        isDefaultPrice: group.isDefaultPrice,
                        price: group.price,
                        isPriceVerified: group.priceVerified,
                    };
                }),
            },
        };
    }

    public mapCreateNewGroupResponse(createGroupDTO: CreateNewGroupDTO): HttpResponse<CreateNewGroupResponseData> {
        return {
            status: HttpStatusEnum.SUCCESS,
            data: {
                groupId: createGroupDTO.id,
                enrollmentKey: createGroupDTO.enrollmentKey,
            },
        };
    }

    public mapJoinExistingGroupResponse(
        joinGroupDTO: JoinExistingGroupDTO
    ): HttpResponse<JoinExistingGroupResponseData> {
        return {
            status: HttpStatusEnum.SUCCESS,
            data: {
                groupId: joinGroupDTO.id,
                groupName: joinGroupDTO.name,
            },
        };
    }

    public mapToGetGroupsListPayload(operations: GroupsListOperations): GroupListPayload {
        return {
            operations: {
                skip: operations.pagination.skip,
                take: operations.pagination.take,
                filteringCriteria: {
                    searchValue: operations.filters.searchValue,
                    groupUserStatus:
                        operations.filters.groupUserStatus === GroupUserStatusEnum.ALL
                            ? null
                            : operations.filters.groupUserStatus,
                    includeDeactivated: operations.filters.includeDeletedGroups,
                    excludeVerifiedPriceGroups: operations.filters.excludeVerifiedPriceGroups,
                },
                sortingCriteria: [
                    {
                        criterion: operations.sort[0].criterion,
                        order: operations.sort[0].order,
                    },
                ],
            },
        };
    }

    public mapGroupGeneralSettingsDTOtoModel(response: GroupGeneralSettingsDTO): HttpResponse<GroupGeneralSettings> {
        return {
            status: HttpStatusEnum.SUCCESS,
            data: {
                canBeDeactivated: response.canBeDeactivated,
                description: response.description ?? '',
                laboratoryName: response.laboratoryName ?? '-',
                name: response.name ?? '',
            },
        };
    }

    public mapGetPrepareData(response: PrepareGroupsListDTO): HttpResponse<PrepareGroupsListData> {
        return {
            status: HttpStatusEnum.SUCCESS,
            data: {
                totalNumberOfGroups: response.totalGroups,
            },
        };
    }

    public mapGetCurrentGroupInfo(response: CurrentGroupInfoDTO): HttpResponse<UserGroup> {
        return {
            status: HttpStatusEnum.SUCCESS,
            data: {
                id: response.id,
                name: response.name,
                isDefault: false,
            },
        };
    }

    public mapGetGroupPricePrepareData(response: GroupPricePrepareDataDTO): HttpResponse<GroupPricePrepareData> {
        return {
            status: HttpStatusEnum.SUCCESS,
            data: {
                defaultPrice: response.defaultPrice,
                groupPrice: response.groupPrice,
                isPriceVerified: response.priceVerified,
            },
        };
    }
}
